/**
 * CORE-746 — central eligibility rule for «Путь света» (gamification).
 *
 * The product rule is deliberately framed as an allow-list, NOT «everyone
 * except masters». Only a regular platform user (a person without the Master
 * role) is a gamification participant. Communities — and any future or
 * service-level subject type the platform grows — are NOT participants and must
 * not become one automatically. When a new subject kind appears, TypeScript's
 * exhaustiveness makes this file the single place a deliberate decision has to
 * be recorded; until then the default is deny.
 *
 * IMPORTANT: gate by the Master *role* (AuthContext `hasRole('Master')` /
 * backend `UserRole.Master`), not by the «Проводник света» level, which shares
 * the wire code `master` but is a level, not a role
 * (docs/requirements/CORE-769-live-capability-contract.md §1.1).
 */

/** A tagged subject the gamification system may or may not admit. */
export type GamificationSubject =
  | { kind: 'user'; isMaster: boolean }
  // Communities are not implemented yet; the branch exists so that when the
  // entity lands it is already excluded without revisiting the base rule.
  | { kind: 'community' }
  // Anything the caller cannot classify (future/service subjects).
  | { kind: 'unknown' };

/**
 * The one place that decides who takes part in «Путь света».
 * Returns true ONLY for a regular, non-master user. Every other subject —
 * masters, communities, unknown types — is denied by default.
 */
export function isGamificationParticipant(subject: GamificationSubject): boolean {
  switch (subject.kind) {
    case 'user':
      return subject.isMaster === false;
    case 'community':
    case 'unknown':
      return false;
    default: {
      // Exhaustiveness guard: a new subject kind must add an explicit,
      // deliberate case above rather than silently defaulting to allowed.
      const _never: never = subject;
      return _never ? false : false;
    }
  }
}

/** Convenience constructor for the common «a person» subject. */
export function userSubject(isMaster: boolean): GamificationSubject {
  return { kind: 'user', isMaster };
}

/**
 * Shorthand for the most common call site: is *this person* (identified only by
 * whether they hold the Master role) a gamification participant?
 */
export function isUserGamificationParticipant(isMaster: boolean): boolean {
  return isGamificationParticipant(userSubject(isMaster));
}
